import { Slot } from '@radix-ui/react-slot';
import {
  Check,
  CircleAlert,
  Copy,
  GripVertical,
  Info,
  TriangleAlert,
  X
} from 'lucide-react';
import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ComponentType,
  createContext,
  forwardRef,
  type ReactNode,
  useContext,
  useEffect,
  useState
} from 'react';
import { Button } from 'v5/design-system/Button';
import {
  InputGroup2,
  InputGroup2Button,
  InputGroup2Input,
  type InputGroup2Props,
  inputGroup2Size
} from 'v5/design-system/InputGroup2';
import type { Input2Tone } from 'v5/design-system/_common/input2Chrome';
import { focusRings } from 'v5/design-system/_common/focusRing';
import { cn } from 'v5/platform/dom/cn';
import { PLACEHOLDER_ROW_KEY, VALUE_PART } from './useRowListKeyboard';

type RowListSize = 'sm' | 'md' | 'lg';

type RowListContextValue = {
  tone: Input2Tone;
  size: RowListSize;
  staticGrip: boolean;
  /** The list has `role="list"`: its rows are its items. */
  isList: boolean;
};

const RowListContext = createContext<RowListContextValue>({
  tone: 'regular',
  size: 'md',
  staticGrip: false,
  isList: false
});

/** The row a part belongs to; `null` outside an editable row. */
const RowKeyContext = createContext<string | null>(null);

export type RowListProps = ComponentProps<'div'> & {
  /** `inline` in a pane (an underline per row, print-like at rest), `regular` in a create dialog (a box per row). */
  tone: Input2Tone;
  size: RowListSize;
  /**
   * The list is editable from the start (a settings page, a create dialog):
   * its handles are there at once. Without it they slide in, for a list that
   * turns editable under the user's eyes.
   */
  staticGrip?: boolean;
};

/**
 * A list of values edited as rows (contact details, locations, options). One
 * grid; every row sits on it as a subgrid, so the lead, value, label and
 * action columns line up across rows, and a column no row uses takes no room.
 * Pair with `useRowListKeyboard` and spread its `listProps` here.
 */
export const RowList = forwardRef<HTMLDivElement, RowListProps>(
  ({ tone, size, staticGrip = false, className, ...props }, ref) => (
    <RowListContext.Provider
      value={{ tone, size, staticGrip, isList: props.role === 'list' }}
    >
      <div
        ref={ref}
        className={cn(
          'grid grid-cols-[auto_auto_minmax(0,1fr)_max-content_auto] content-start items-center',
          tone === 'regular' && 'gap-y-2',
          // Inline rows start at the field's edge, under its title.
          tone !== 'regular' && '[&_[data-row-column=lead]]:pl-0',
          // A host that indents its content by `--lead-gutter` gets the leads
          // in that room, so values line up with its text in both modes: at
          // rest the leads hang there; edited, the rows clip what sticks out,
          // so the whole list moves there when its rows have leads. A boxed
          // row keeps its lead inside its box.
          tone === 'plain' && '[&_[data-row-column=lead]]:-ml-(--lead-gutter)',
          tone === 'inline' &&
            'has-[[data-row-column=lead]]:-ml-(--lead-gutter)',
          className
        )}
        {...props}
      />
    </RowListContext.Provider>
  )
);
RowList.displayName = 'RowList';

export type RowListRowProps = Omit<
  InputGroup2Props,
  'tone' | 'size' | 'leftSlot' | 'rightSlot'
> & {
  /**
   * The row's identity for the keyboard (`data-row-key` on its parts).
   * `PLACEHOLDER_ROW_KEY` makes it the empty row at the end: render it under
   * the React key the new row will have, in the same array as the rows and
   * with its input keyed, so the input stays the same node (and keeps the
   * caret) when the first keystroke turns it into a row.
   */
  rowKey: string;
  /** Added in this edit and not saved yet. */
  fresh?: boolean;
};

/** One editable row: an `InputGroup2` spanning the list's columns. */
export const RowListRow = forwardRef<HTMLDivElement, RowListRowProps>(
  ({ rowKey, variant, fresh, className, ...props }, ref) => {
    const { tone, size, isList } = useContext(RowListContext);
    const placeholder = rowKey === PLACEHOLDER_ROW_KEY;
    return (
      <RowKeyContext.Provider value={rowKey}>
        <InputGroup2
          ref={ref}
          // In a list every row is an item, the empty one at the end too.
          role={isList ? 'listitem' : 'group'}
          // The empty row shows no underline until it is hovered or focused.
          tone={placeholder && tone === 'inline' ? 'plain' : tone}
          variant={variant}
          size={size}
          className={cn(
            'group/row col-span-full grid grid-cols-subgrid',
            fresh && 'bg-bg-accent/20',
            placeholder &&
              tone === 'regular' &&
              'border-dashed bg-transparent focus-within:border-solid',
            placeholder && '[&_[data-row-column=lead]]:opacity-45',
            className
          )}
          {...props}
        />
      </RowKeyContext.Provider>
    );
  }
);
RowListRow.displayName = 'RowListRow';

/** A row at rest (display mode): no chrome, the same columns. */
export const RowListDisplayRow = forwardRef<
  HTMLDivElement,
  ComponentProps<'div'>
>(({ className, ...props }, ref) => {
  const { size } = useContext(RowListContext);
  return (
    <div
      ref={ref}
      className={cn(
        inputGroup2Size({ size }),
        'group/row col-span-full grid grid-cols-subgrid items-center',
        className
      )}
      {...props}
    />
  );
});
RowListDisplayRow.displayName = 'RowListDisplayRow';

type RowListColumn = 'grip' | 'lead' | 'value' | 'label' | 'action';

const COLUMN_START: Record<RowListColumn, string> = {
  grip: 'col-start-1',
  lead: 'col-start-2',
  value: 'col-start-3',
  label: 'col-start-4',
  action: 'col-start-5'
};

export type RowListPartProps = ComponentPropsWithoutRef<typeof Slot> & {
  column: RowListColumn;
  /** Names a control for the keyboard (`data-row-part`): the part Enter moves to on an error, the part ↑ / ↓ keep. */
  part?: string;
};

/**
 * Places its child in a column of the row. With `part`, the child is a control
 * the row keyboard knows (a text input or a picker's trigger).
 */
export const RowListPart = forwardRef<HTMLElement, RowListPartProps>(
  ({ column, part, className, ...props }, ref) => {
    const rowKey = useContext(RowKeyContext);
    const marksControl = part !== undefined && rowKey !== null;
    return (
      <Slot
        ref={ref}
        data-row-column={column}
        data-row-key={marksControl ? rowKey : undefined}
        data-row-part={marksControl ? part : undefined}
        className={cn(COLUMN_START[column], 'min-w-0', className)}
        {...props}
      />
    );
  }
);
RowListPart.displayName = 'RowListPart';

/** The row's text input (the value column unless placed elsewhere). */
export const RowListInput = forwardRef<
  HTMLInputElement,
  ComponentProps<'input'> & { part?: string }
>(({ part = VALUE_PART, className, ...props }, ref) => {
  const { tone } = useContext(RowListContext);
  return (
    <RowListPart column="value" part={part}>
      <InputGroup2Input
        ref={ref}
        type="text"
        autoComplete="off"
        className={cn(
          'self-stretch pr-2',
          tone !== 'regular' && 'pl-0',
          className
        )}
        {...props}
      />
    </RowListPart>
  );
});
RowListInput.displayName = 'RowListInput';

/** A lead that is only a sign (a map pin), not a picker. */
export function RowListLead({
  className,
  children,
  ...props
}: ComponentProps<'span'>) {
  return (
    <RowListPart column="lead">
      <span
        className={cn('flex items-center self-stretch pr-2 pl-2.5', className)}
        {...props}
      >
        <RowListLeadIcon>{children}</RowListLeadIcon>
      </span>
    </RowListPart>
  );
}

/** A lead that is a muted icon only (a pin, an email's type). */
export function RowListLeadSign({
  icon: Icon
}: {
  icon: ComponentType<{ className?: string }>;
}) {
  return (
    <RowListLead aria-hidden>
      <Icon className="text-fg-muted" />
    </RowListLead>
  );
}

/**
 * A fixed-size slot for the lead's icon or flag, so a row never shifts when it
 * changes. Its icon is smaller than the unit title's: the values are the
 * second level under it.
 */
export function RowListLeadIcon({
  className,
  ...props
}: ComponentProps<'span'>) {
  const { size } = useContext(RowListContext);
  return (
    <span
      className={cn(
        'inline-flex size-5 shrink-0 items-center justify-center',
        LEAD_ICON_SIZE[size],
        className
      )}
      {...props}
    />
  );
}

// Important: the row's own size (`inputGroup2Size`) sizes every svg in it.
// Smaller than the unit title's icon (`UnitTitleIcon`, `size-4`).
const LEAD_ICON_SIZE: Record<RowListSize, string> = {
  sm: '[&_svg]:size-3!',
  md: '[&_svg]:size-3.5!',
  lg: '[&_svg]:size-4!'
};

export type RowListLabelSizerProps = {
  /** Every option's text: the column keeps the longest one's width (up to 5rem) whichever is picked. */
  options: ReadonlyArray<string>;
  /** The picked option's text, or a placeholder. */
  children: ReactNode;
  /** Shown after the text (the primary email's chip); only this row's cell grows for it. */
  mark?: ReactNode;
};

/** The label picker's content, as wide as its longest option so picking another one moves nothing. */
export function RowListLabelSizer({
  options,
  children,
  mark
}: RowListLabelSizerProps) {
  // The outer span is the one a select trigger's value slot turns into `display: contents`.
  return (
    <span>
      <span className="grid min-w-0 text-left">
        {options.map(option => (
          <span
            key={option}
            aria-hidden="true"
            className="invisible max-w-20 truncate [grid-area:1/1]"
          >
            {option}
          </span>
        ))}
        <span className="flex min-w-0 items-center gap-1 [grid-area:1/1]">
          <span className="max-w-20 truncate">{children}</span>
          {mark}
        </span>
      </span>
    </span>
  );
}

/**
 * The row's drag handle, in a column of its own before the lead (a swatch, a
 * sign). Not a keyboard part: Space / Enter picks the row up (⌥↑ / ⌥↓ move it
 * from anywhere in the row).
 */
export const RowListGrip = forwardRef<
  HTMLButtonElement,
  ComponentProps<'button'> & { 'aria-label': string }
>(({ className, ...props }, ref) => {
  const { tone, staticGrip } = useContext(RowListContext);
  return (
    <RowListPart column="grip">
      <button
        ref={ref}
        type="button"
        aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
        className={cn(
          'flex cursor-grab touch-none items-center self-stretch overflow-hidden text-fg-muted select-none active:cursor-grabbing',
          // In a box the handle is a target of its own, clear of the border.
          tone === 'regular' ? 'w-9 justify-center pl-1' : 'w-5',
          // Grows in when the list turns editable, so the values slide aside instead of jumping.
          !staticGrip &&
            'transition-[width,opacity] duration-200 starting:w-0 starting:opacity-0',
          'disabled:invisible',
          'hocus:text-fg-default [&_svg]:size-4',
          focusRings.focus.visible,
          // The handle fills the row's edge and the row clips what is outside
          // it, so the ring is drawn inside.
          'not-disable-focus-ring:not-aria-readonly:focus-visible:ring-inset not-disable-focus-ring:not-aria-readonly:focus-visible:ring-offset-0',
          tone === 'regular' && 'rounded-l-[3px]',
          className
        )}
        {...props}
      >
        <GripVertical />
      </button>
    </RowListPart>
  );
});
RowListGrip.displayName = 'RowListGrip';

/**
 * Removes the row; shown while the row is hovered or focused, always on touch. Out of the Tab
 * order, so Tab goes from a row's value on to the next row: ⌘⌫ removes from
 * anywhere in the row.
 */
export const RowListRemove = forwardRef<
  HTMLButtonElement,
  ComponentProps<'button'> & { 'aria-label': string }
>(({ className, ...props }, ref) => {
  const { tone } = useContext(RowListContext);
  return (
    <RowListPart column="action">
      <InputGroup2Button
        ref={ref}
        tabIndex={-1}
        aria-keyshortcuts="Meta+Backspace Control+Backspace"
        className={cn(
          'aspect-square justify-center self-stretch text-fg-muted opacity-0 transition-opacity',
          'group-hocus/row:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100 hocus:text-fg-default',
          tone === 'regular' && 'rounded-r border-l border-border-default',
          className
        )}
        {...props}
      >
        <X />
      </InputGroup2Button>
    </RowListPart>
  );
});
RowListRemove.displayName = 'RowListRemove';

/** Copies a row's value at rest; shown while the row is hovered or focused, always on touch. */
export function RowListCopy({
  value,
  'aria-label': ariaLabel
}: {
  value: string;
  'aria-label': string;
}) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);
  return (
    <RowListPart column="action">
      <InputGroup2Button
        aria-label={ariaLabel}
        className="aspect-square justify-center self-stretch text-fg-muted opacity-0 transition-opacity group-hocus/row:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100 hocus:text-fg-default"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
          } catch {
            setCopied(false);
          }
        }}
      >
        {copied ? <Check /> : <Copy />}
      </InputGroup2Button>
    </RowListPart>
  );
}

type RowListMessageTone = 'error' | 'hint' | 'info';

const MESSAGE_ICON: Record<RowListMessageTone, ReactNode> = {
  error: <CircleAlert className="size-3.5" />,
  hint: <TriangleAlert className="size-3.5 text-fg-attention" />,
  info: <Info className="size-3.5" />
};

export type RowListMessageProps = ComponentProps<'div'> & {
  /** `error` blocks Save; `hint` and `info` never do. */
  tone: RowListMessageTone;
  /** Referenced by the row's controls through `aria-describedby`. */
  id: string;
};

/** A line under a row, spanning it: what is wrong or worth a look, and what to do about it. */
export function RowListMessage({
  tone,
  className,
  children,
  ...props
}: RowListMessageProps) {
  return (
    <div
      className={cn(
        'col-span-full grid grid-cols-[0.875rem_minmax(0,1fr)] items-start gap-x-2 pt-1 pb-2 text-base',
        tone === 'error' ? 'font-medium text-fg-danger' : 'text-fg-muted',
        className
      )}
      {...props}
    >
      <span className="flex h-lh items-center">{MESSAGE_ICON[tone]}</span>
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        {children}
      </div>
    </div>
  );
}

/** A one-click fix offered in a message (“Use +44 7891 546888”, “Keep as pasted”). */
export function RowListMessageAction(
  props: Omit<ComponentProps<typeof Button>, 'variant' | 'size'>
) {
  return <Button variant="secondary" size="xs" {...props} />;
}
